import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/** 캡쳐 시 고정할 너비(px). 뷰포트 폭에 따라 결과가 달라지지 않도록 한다. */
const CAPTURE_WIDTH = 800;
/** A4 너비(mm) */
const A4_WIDTH_MM = 210;
/** PDF 여백(mm) */
const MARGIN_MM = 10;
/** 캡쳐 배율. 2 = 고해상도 */
const DEFAULT_SCALE = 2;

export interface DownloadPdfOptions {
  /** 캡쳐 배율. 기본 2 */
  scale?: number;
  /** 캡쳐 시 고정할 너비(px). 기본 800 */
  captureWidth?: number;
}

/**
 * 지정한 엘리먼트를 캡쳐해 A4 너비 기준 단일 페이지 PDF 로 저장한다.
 * 높이는 콘텐츠 비율에 맞춰 동적으로 결정되므로 내용이 잘리지 않는다.
 *
 * @param elementId 캡쳐 대상 엘리먼트의 id (# 없이)
 * @param fileName  저장할 파일명. .pdf 가 없으면 자동으로 붙는다
 * @returns 저장 성공 여부
 */
export async function downloadElementAsPdf(
  elementId: string,
  fileName: string,
  options: DownloadPdfOptions = {}
): Promise<boolean> {
  const { scale = DEFAULT_SCALE, captureWidth = CAPTURE_WIDTH } = options;

  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`PDF 대상 엘리먼트를 찾을 수 없습니다: #${elementId}`);
    return false;
  }

  const originalStyle = element.style.cssText;
  try {
    // 캡쳐를 위해 임시로 너비 고정
    element.style.width = `${captureWidth}px`;
    element.style.maxWidth = `${captureWidth}px`;

    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png");

    // PDF 너비는 A4 기준, 높이는 콘텐츠 비율에 맞춰 동적으로 설정
    const contentWidth = A4_WIDTH_MM - MARGIN_MM * 2;
    const contentHeight = (canvas.height * contentWidth) / canvas.width;

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [A4_WIDTH_MM, contentHeight + MARGIN_MM * 2],
    });

    // 한 페이지에 전체 이미지 삽입
    pdf.addImage(
      imgData,
      "PNG",
      MARGIN_MM,
      MARGIN_MM,
      contentWidth,
      contentHeight
    );

    pdf.save(fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`);
    return true;
  } catch (error) {
    console.error("PDF 다운로드 오류:", error);
    return false;
  } finally {
    // 캡쳐 실패 여부와 관계없이 반드시 복원한다.
    // (복원이 try 안에 있으면 html2canvas 실패 시 너비 고정이 남아 레이아웃이 깨진다)
    element.style.cssText = originalStyle;
  }
}
